import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "url-search-params-polyfill";
import SkeletonCard from "./SkeletonCard";
import Angle from "../svg/Angle";
import Search from "../shared/Search";
import handleFilterChange from "../../utils/handleFilterChange";
import getSearchResults from "../../lib/api/searchApi";
import SearchResult from "./SearchResult";
import { ISearchResults } from "../../lib/models/ISearchResults";
import Pagination from "../shared/Pagination";

export default function SearchPage({
  currentPageRef,
  pageSize,
  searchFor,
  placeholder,
  noResults
}) {
  const urlParams = new URLSearchParams(window.location.search);

  const initialFilters = {
    page: parseInt(urlParams.get("page")) || 1,
    sort: urlParams.get("sort") || ""
  };

  const initialSearchValue = urlParams.get("q") || "";

  const sortTypes = [
    { text: "Most Relevant", value: "relevancy" },
    { text: "Most Recent", value: "date" }
  ];

  const [filters, updateFilters] = useState(initialFilters);
  const [searchValue, updateSearchValue] = useState(initialSearchValue);
  const [finalSearchValue, updateFinalSearchValue] = useState(initialSearchValue);
  const [loading, updateLoading] = useState(true);
  const [results, updateResults]: [
    ISearchResults,
    React.Dispatch<any>
  ] = useState(null);

  const loadSearchResults = () => {
    updateLoading(true);
    syncVisible();
    getSearchResults(currentPageRef, finalSearchValue, filters.page, filters.sort)
      .then((e) => {
        updateResults(e);
        updateLoading(false);
      })
      .catch((e) => {
        console.error(e);
        return toast.error("Something went wrong. Please try again later");
      });
  };

  const syncFinal = () => {
    updateFilters({ ...filters, page: 1 });
    updateFinalSearchValue(searchValue);
  };
  const syncVisible = () => updateSearchValue(finalSearchValue);

  useEffect(loadSearchResults, [filters, finalSearchValue]);

  const renderResults = () => {
    if (!loading) {
      return results?.contentModel?.tniHits?.map((e, i) => (
        <SearchResult
          key={e.url}
          number={i}
          title={e.title}
          excerpt={e.excerpt}
          url={e.url}
          parentPage={e.parentPage}
        />
      ));
    }
    return [...Array(pageSize + 1)].map((e, i) => <SkeletonCard key={i} />);
  };

  return (
    <>
      <ToastContainer />
      <Angle position="bottom" />
      <div className="header">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h5>{searchFor}</h5>
              <Search
                searchValue={searchValue}
                changeSearchValue={updateSearchValue}
                doSearch={syncFinal}
                label="Website"
                className="site-search"
                placeholder={placeholder}
              />
            </div>
          </div>
        </div>
      </div>
      <Angle position="top" />
      <div className="container">
        <div className="row results-filters align-items-center">
          <div className="col-12 col-sm">
            <p className="large mb-sm-0">
              {results?.contentModel?.numberOfHits || 0} results
            </p>
          </div>
          <div className="col flex-sm-grow-0 sort">
            <select
              onChange={(e) => handleFilterChange(
                "replace",
                { sort: e.target.value },
                filters,
                updateFilters
              )
              }
            >
              {sortTypes.map((e, i) => (
                <option value={e.value} key={i}>
                  {e.text}
                </option>
              ))}
            </select>
          </div>
        </div>
        {!loading && results?.contentModel.didYouMean.hits.length > 0 && (
          <div className="row">
            <div className="col-12">
              <p className="didyoumean large">
                Did you mean:{" "}
                {results.contentModel.didYouMean.hits.map(({ suggestion }, i) => (
                  <a
                    href={`?q=${encodeURIComponent(suggestion)}`}
                    key={suggestion}
                    onClick={(e) => {
                      e.preventDefault();
                      updateFinalSearchValue(suggestion);
                    }}
                  >
                    <span>{suggestion}</span>{i !== results.contentModel.didYouMean.hits.length - 1 && ", "}
                  </a>
                ))}
              </p>
            </div>
          </div>
        )}
        <div className="row">
          {renderResults()}
          {!results?.contentModel.numberOfHits ? (
            <div className="col-12">
              <div
                className="no-results"
                dangerouslySetInnerHTML={{
                  __html: noResults
                }}
              ></div>
            </div>
          ) : null}
        </div>
        <nav
          className="row justify-content-center pagination"
          role="navigation"
          aria-label="Pagination navigation"
        >
          {results && (
            <Pagination
              pageSize={pageSize}
              totalItems={results.contentModel.numberOfHits}
              currentPage={filters.page}
              updatePage={handleFilterChange}
              filters={filters}
              updateFilters={updateFilters}
            />
          )}
        </nav>
      </div>
    </>
  );
}
